import React, { useState, useRef, useEffect } from "react";
import { storeAuthToken, storeUserInfo } from "../../utils/authUtils";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserType, loginUser, setLoginStep,
  forgotOtp, verifyOTP, resetPassword, resendOtp, clearError,
} from "../../store/slices/auth.slice";
import {
  Box, Typography, TextField, Button, Paper,
  IconButton, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from "@mui/material";
import Swal from "sweetalert2";
import {
  Visibility, VisibilityOff, Close, CheckCircle,
  AssignmentOutlined, PsychologyOutlined, MapOutlined, BarChartOutlined,
} from "@mui/icons-material";
import getLogoPath from "../../utils/logoUtils";
import theme from "../../Theme.config";

const logoImg = getLogoPath();

const C = {
  navy:  "#1F2A44",
  blue:  "#4A90E2",
  pink:  "#E91E63",
  sky:   "#2F80ED",
};

export default function LoginPage() {
  const [formData, setFormData]               = useState({ username: "", password: "" });
  const [showPassword, setShowPassword]       = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const recaptchaRef                          = useRef(null);
  const [openForgot, setOpenForgot]           = useState(false);
  const [step, setStep]                       = useState("forgot");
  const [email, setEmail]                     = useState("");
  const [otp, setOtp]                         = useState(["","","","","",""]);
  const [newPassword, setNewPassword]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPw, setShowNewPw]             = useState(false);
  const [showConfPw, setShowConfPw]           = useState(false);
  const [passwordError, setPasswordError]     = useState("");

  useEffect(() => {
    setPasswordError(newPassword && confirmPassword && newPassword !== confirmPassword
      ? "Passwords do not match" : "");
  }, [newPassword, confirmPassword]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, loginStep, error } = useSelector((s) => s.auth);

  const handleInputChange  = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleForgotClose  = () => { setOpenForgot(false); setStep("forgot"); setOtp(["","","","","",""]); setNewPassword(""); setConfirmPassword(""); setPasswordError(""); };
  const handleToLogin      = () => { setOpenForgot(false); navigate("/login"); setStep("forgot"); };
  const handleBackToUsername = () => {
    dispatch(setLoginStep("username")); dispatch(clearError());
    setFormData((p) => ({ ...p, password: "" })); setCaptchaVerified(false);
    recaptchaRef.current?.reset();
  };

  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !captchaVerified) return;
    try { const res = await dispatch(getUserType(formData.username)).unwrap(); if (res?.email) setEmail(res.email); } catch {}
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await dispatch(loginUser({ username: formData.username, password: formData.password })).unwrap();
      if (result?.tokens) {
        storeAuthToken(result.tokens.access_token, result.tokens.refresh_token, result.tokens.expires_in || 3600);
        storeUserInfo(result.user);
        setFormData({ username: "", password: "" });
        if (result.user?.is_first_login === true || result.user?.is_first_login === "true") {
          Swal.fire({ icon: "info", title: "First Time Login", text: "Please change your password.", confirmButtonText: "Change Password", allowOutsideClick: false, allowEscapeKey: false, allowEnterKey: true, confirmButtonColor: theme.swal?.confirmButtonColor || theme.palette.primary.main }).then(() => navigate("/user-management/change-password"));
        } else { navigate("/dashboard"); }
      }
    } catch {}
  };

  const handleSendOtp    = () => { if (!email || !formData.username) return; try { dispatch(forgotOtp({ username: formData.username, email })); setStep("otp"); } catch {} };
  const handleResendOtp  = async () => {
    if (!formData.username) return; setOtp(["","","","","",""]);
    try { const res = await dispatch(resendOtp({ username: formData.username })).unwrap(); if (res?.message) Swal.fire({ icon: "success", title: "Success", text: res.message, confirmButtonText: "OK", confirmButtonColor: theme.swal?.confirmButtonColor || theme.palette.primary.main }); } catch {}
  };
  const handleOtpChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    if (val.length > 1 && idx === 0) {
      const digits = val.split("").slice(0, 6); const next = [...otp]; digits.forEach((d, i) => { next[i] = d; }); setOtp(next);
      document.getElementById(`otp-input-${digits.length - 1}`)?.focus(); return;
    }
    if (val.length > 1) return;
    const next = [...otp]; next[idx] = val; setOtp(next);
    if (val && idx < 5) document.getElementById(`otp-input-${idx + 1}`)?.focus();
  };
  const handleOtpKeyDown = (e, idx) => { if (e.key === "Backspace" && otp[idx] === "" && idx > 0) document.getElementById(`otp-input-${idx - 1}`)?.focus(); };
  const handleVerifyOtp = async () => { try { await dispatch(verifyOTP({ username: formData.username, otp_code: otp.join("") })).unwrap(); setStep("reset"); } catch { setPasswordError("Failed. Please check OTP and try again."); } };
  const handleResetPassword = async () => { try { await dispatch(resetPassword({ username: formData.username, otp_code: otp.join(""), new_password: newPassword })).unwrap(); setStep("success"); } catch { setPasswordError("Failed to reset password. Please check OTP and try again."); } };

  // ── shared field style ────────────────────────────────────────────────────
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      bgcolor: "#f9fafb",
      "& fieldset":             { borderColor: "#e5e7eb" },
      "&:hover fieldset":       { borderColor: C.blue },
      "&.Mui-focused fieldset": { borderColor: C.blue, borderWidth: "1.5px" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: C.blue },
  };

  const primaryBtn = {
    py: 1.3, borderRadius: "8px",
    background: `linear-gradient(135deg, ${C.navy} 0%, #2a3a5c 100%)`,
    fontSize: "0.95rem", fontWeight: 600, textTransform: "none",
    boxShadow: "0 2px 8px rgba(31,42,68,0.25)",
    "&:hover":    { background: `linear-gradient(135deg, #2a3a5c 0%, ${C.navy} 100%)`, boxShadow: "0 4px 14px rgba(31,42,68,0.35)" },
    "&:disabled": { bgcolor: "#e5e7eb", color: "#9ca3af", background: "none", boxShadow: "none" },
  };

  // ── features ────────────────────────────────────────────────────────────
  const features = [
    { Icon: AssignmentOutlined, color: C.blue,  title: "Dynamic Questionnaires",  desc: "GL, WC & CA lines with smart coverage rules" },
    { Icon: PsychologyOutlined, color: "#a78bfa", title: "AI-Powered Processing",  desc: "Automated document extraction & risk analytics" },
    { Icon: MapOutlined,        color: "#34d399", title: "50-State Compliance",     desc: "State-specific regulations & cross-state filings" },
    { Icon: BarChartOutlined,   color: C.pink,  title: "Real-Time Analytics",     desc: "Live dashboards and automated audit reporting" },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background: `linear-gradient(150deg, ${C.navy} 0%, #253352 50%, #1a2540 100%)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ── decorative circles ─────────────────────────────────────────────── */}
      <Box sx={{ position: "absolute", top: -160, left: -160, width: 500, height: 500, borderRadius: "50%", border: `1px solid rgba(74,144,226,0.1)`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", top: -80,  left: -80,  width: 300, height: 300, borderRadius: "50%", border: `1px solid rgba(74,144,226,0.08)`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -200, right: -200, width: 600, height: 600, borderRadius: "50%", border: `1px solid rgba(233,30,99,0.08)`, pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -100, right: -100, width: 360, height: 360, borderRadius: "50%", border: `1px solid rgba(233,30,99,0.06)`, pointerEvents: "none" }} />

      {/* ── LEFT – branding ────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          width: "50%",
          p: "56px 64px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box>
          <Box sx={{ mb: 5 }} />

          {/* Headline */}
          <Typography sx={{ fontSize: "2.15rem", fontWeight: 700, color: "#fff", lineHeight: 1.25, mb: 2.5 }}>
            Submission Intake<br />Management System
          </Typography>

          {/* Sub */}
          <Typography sx={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.55)", lineHeight: 1.75, mb: 5 }}>
            End-to-end platform for dynamic questionnaires,<br />
            AI-powered document processing, and multi-state<br />
            compliance management.
          </Typography>

          {/* Feature list */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {features.map(({ Icon, color, title, desc }, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex", alignItems: "center", gap: 2,
                  p: "11px 16px", borderRadius: "10px",
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  transition: "all 0.2s",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.07)", borderColor: "rgba(255,255,255,0.14)" },
                }}
              >
                <Box sx={{ width: 34, height: 34, borderRadius: "8px", bgcolor: `${color}22`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon sx={{ fontSize: "1.1rem", color }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#fff", mb: 0.15, lineHeight: 1.3 }}>{title}</Typography>
                  <Typography sx={{ fontSize: "0.76rem", color: "rgba(255,255,255,0.42)", lineHeight: 1.4 }}>{desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.28)" }}>
          © 2026 iNube · Terms and Conditions Applied
        </Typography>
      </Box>

      {/* ── RIGHT – login card ─────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, md: "48px 72px 48px 32px" },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 420,
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            bgcolor: "#fff",
          }}
        >
          {/* 4px gradient top bar */}
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${C.blue} 0%, ${C.pink} 100%)` }} />

          <Box sx={{ p: { xs: "32px 28px", md: "36px 40px" } }}>
            {/* Logo centered at top */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <img src={logoImg} alt="iNube" style={{ maxHeight: 40, width: "auto" }} />
            </Box>

            {/* Title */}
            <Typography sx={{ fontWeight: 700, fontSize: "1.45rem", color: C.navy, textAlign: "center", mb: 0.5 }}>
              Welcome Back
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: "#6b7280", textAlign: "center", mb: 3.5 }}>
              Sign in to continue to your account
            </Typography>

            {/* ── USERNAME ── */}
            {loginStep === "username" ? (
              <Box component="form" onSubmit={handleUsernameSubmit}>
                <TextField
                  fullWidth size="small" label="Username" name="username"
                  value={formData.username} onChange={handleInputChange}
                  placeholder="Enter your username" required
                  sx={{ ...fieldSx, mb: 2.5 }}
                />

                <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5, transform: "scale(0.85)", transformOrigin: "center" }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                    onChange={(t) => setCaptchaVerified(!!t)}
                    onExpired={() => setCaptchaVerified(false)}
                  />
                </Box>

                {error && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px" }}>
                    <Typography sx={{ color: "#dc2626", fontSize: "0.82rem" }}>{error}</Typography>
                  </Box>
                )}

                <Button type="submit" fullWidth variant="contained" disabled={isLoading || !formData.username || !captchaVerified} sx={primaryBtn}>
                  {isLoading ? "Processing…" : "Continue →"}
                </Button>
              </Box>
            ) : (
              /* ── PASSWORD ── */
              <Box component="form" onSubmit={handlePasswordSubmit}>
                {/* User badge */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, px: 2, py: 1.25, bgcolor: "#f0f9ff", borderRadius: "8px", border: `1px solid #bae6fd` }}>
                  <Box>
                    <Typography sx={{ fontSize: "0.68rem", color: "#64748b" }}>Logging in as</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: C.navy }}>{formData.username}</Typography>
                  </Box>
                  <Button variant="text" size="small" onClick={handleBackToUsername} sx={{ color: C.sky, textTransform: "none", fontSize: "0.8rem", minWidth: "auto", p: 0, "&:hover": { bgcolor: "transparent", textDecoration: "underline" } }}>
                    Change
                  </Button>
                </Box>

                <TextField
                  fullWidth size="small" label="Password" name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password} onChange={handleInputChange}
                  placeholder="Enter your password" required autoFocus
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )}}
                  sx={{ ...fieldSx, mb: 1 }}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                  <Typography onClick={() => setOpenForgot(true)} sx={{ cursor: "pointer", color: C.sky, fontSize: "0.82rem", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}>
                    Forgot Password?
                  </Typography>
                </Box>

                {error && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px" }}>
                    <Typography sx={{ color: "#dc2626", fontSize: "0.82rem" }}>{error}</Typography>
                  </Box>
                )}

                <Button type="submit" fullWidth variant="contained" disabled={isLoading || !formData.password} sx={primaryBtn}>
                  {isLoading ? "Signing in…" : "Sign In →"}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* ── FORGOT PASSWORD DIALOG ──────────────────────────────────────────── */}
      <Dialog open={openForgot} maxWidth="sm" fullWidth sx={{ textAlign: "center" }}>
        <Box sx={{ p: "50px 40px", minHeight: 400, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", position: "relative" }}>
          <IconButton onClick={handleForgotClose} sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}>
            <Close />
          </IconButton>

          <DialogContent sx={{ flex: "unset", width: "100%" }}>
            <DialogTitle sx={{ m: 0, p: 2 }}>
              {step === "forgot" ? "Forgot Password" : step === "otp" ? "Enter OTP" : step === "reset" ? "Reset Password" : null}
            </DialogTitle>

            {step === "forgot" && (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>Please enter your registered Email ID to reset the password</Typography>
                <TextField autoFocus label="Email Address" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} InputProps={{ readOnly: true }} />
              </>
            )}
            {step === "otp" && (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>Enter the OTP sent to your Email ID</Typography>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, my: 2 }}>
                  {otp.map((val, i) => (
                    <TextField key={i} id={`otp-input-${i}`} value={val} onChange={(e) => handleOtpChange(e, i)} onKeyDown={(e) => handleOtpKeyDown(e, i)}
                      inputProps={{ maxLength: 1, inputMode: "numeric", pattern: "[0-9]*", style: { textAlign: "center", fontSize: "20px" } }}
                      sx={{ width: "48px" }} />
                  ))}
                </Box>
                <Box textAlign="center" mb={2}>
                  <Typography component="a" href="#" onClick={(e) => { e.preventDefault(); handleResendOtp(); }} sx={{ color: C.sky, fontSize: "0.875rem", cursor: "pointer" }}>Resend OTP</Typography>
                </Box>
              </>
            )}
            {step === "reset" && (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>Please reset your password</Typography>
                <TextField autoFocus label="New Password" type={showNewPw ? "text" : "password"} fullWidth sx={{ mb: 2 }} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowNewPw((s) => !s)} edge="end">{showNewPw ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
                <TextField label="Confirm Password" type={showConfPw ? "text" : "password"} fullWidth value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowConfPw((s) => !s)} edge="end">{showConfPw ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
                {passwordError && <Typography color="error" sx={{ mt: 1, fontSize: "0.85rem" }}>{passwordError}</Typography>}
              </>
            )}
            {step === "success" && (
              <Box sx={{ textAlign: "center" }}>
                <CheckCircle sx={{ fontSize: 60, color: "green" }} />
                <Typography variant="h6" sx={{ mt: 2 }}>Password has been reset successfully!</Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", gap: 2 }}>
            <Button onClick={handleForgotClose} variant="outlined" color="error" sx={{ borderRadius: 25 }}>Cancel</Button>
            {step === "forgot"   && <Button onClick={handleSendOtp}        variant="contained" sx={{ borderRadius: 25 }} disabled={!email}>Send OTP</Button>}
            {step === "otp"      && <Button onClick={handleVerifyOtp}      variant="contained" sx={{ borderRadius: 25 }} disabled={otp.some((v) => v === "")}>Submit</Button>}
            {step === "reset"    && <Button onClick={handleResetPassword}  variant="contained" sx={{ borderRadius: 25 }} disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}>Reset Password</Button>}
            {step === "success"  && <Button onClick={handleToLogin}        variant="contained" sx={{ borderRadius: 25 }}>Login</Button>}
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
